import { useCreateConstructionAdderMutation } from '@/services/pricing';
import { useSearchProductsMutation } from '@/services/productCatalog';
import { Box, Button, FormikTextInput, Modal, Spinner, toast } from '@palmetto/palmetto-components';
import { Field, FieldArray, Form, Formik } from 'formik';
import { SyntheticEvent, useCallback, useEffect, useState } from 'react';

import { adderMap, AdderType, ConstructionAdderType, ProgramType } from 'types';

interface EnergyBackupCapValues {
  name: string;
  energyBackupCaps: Record<string, Record<string, any>>;
}

export const CreatePriceCapModal = ({
  isModalOpen,
  closeModal,
  adderType,
  programType,
}: {
  isModalOpen: boolean;
  closeModal: (event?: SyntheticEvent) => void;
  adderType?: AdderType;
  programType: ProgramType;
}) => {
  const [allManufacturers, setAllManufacturers] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [triggerSearch] = useSearchProductsMutation();
  const [createConstructionAdder] = useCreateConstructionAdderMutation();

  const getAllManufacturers = useCallback(
    async (page = 1, data: string[] = []) => {
      setIsLoading(true);
      try {
        const response = await triggerSearch({
          limit: 20,
          page: page,
          filters: [
            {
              filter: 'classificationPath',
              operation: {
                equals: 'solar/energy-storage/backup',
              },
            },
            {
              filter: 'attribute',
              key: 'lightreach-energy-backup-enabled',
              operation: {
                equals: true,
              },
            },
          ],
        }).unwrap();
        const manufacturerSet = new Set<string>(data);
        response.data.forEach((item: any) => {
          if (item.manufacturer) {
            manufacturerSet.add(item.manufacturer);
          }
        });
        const manufacturers = Array.from(manufacturerSet);
        if (response.paging?.totalPages && response.paging.page < response.paging.totalPages) {
          await getAllManufacturers(page + 1, manufacturers);
        } else {
          setIsLoading(false);
          setAllManufacturers(manufacturers.toSorted());
        }
      } catch (_err: any) {
        setIsLoading(false);
      }
    },
    [triggerSearch],
  );

  useEffect(() => {
    getAllManufacturers();
  }, [getAllManufacturers]);

  const handleSubmit = useCallback(
    async (values: EnergyBackupCapValues) => {
      const key =
        adderType === AdderType.arbitrageBatteryPriceCap
          ? ConstructionAdderType.arbitrageBatteryPriceCap
          : ConstructionAdderType.backupBatteryPriceCap;

      const data = Object.entries(values.energyBackupCaps ?? {}).reduce(
        (acc, [manufacturer, value]) => {
          acc.push({
            maxPrice: value.maxPrice,
            maxTermLength: value.maxTermLength,
            manufacturer,
          });
          return acc;
        },
        [] as { maxPrice: number; maxTermLength: number; manufacturer: string }[],
      );

      try {
        await createConstructionAdder({
          data: {
            adderType: key,
            name: values.name,
            energyBackupCaps: data,
          },
          programType: programType,
        }).unwrap();
        toast.success('Adder created successfully');
        closeModal();
      } catch (e) {
        console.error(e);
        toast.error('Error creating adder');
      }
    },
    [adderType, closeModal, createConstructionAdder, programType], // Ensure createConstructionAdder is defined in the scope
  );

  return (
    <Modal ariaLabelledBy="createEnergyBackupCaps" isOpen={isModalOpen} onDismiss={closeModal} maxWidth="500px">
      <Modal.Header
        id="createEnergyBackupCaps"
        title={`Add ${adderMap[programType][adderType as AdderType]} Modifier`}
        onDismiss={closeModal}
      />
      {isLoading && <Spinner size="md" />}
      <Formik initialValues={{} as any} onSubmit={handleSubmit} enableReinitialize>
        {({ resetForm, isSubmitting }) => {
          return (
            <Form>
              <Modal.Body overflow={'auto'} maxHeight={'70vh'} background="secondary">
                <Box direction="column" childGap="md">
                  <Field component={FormikTextInput} label="Option Name" name="name" id="name" isRequired />
                  <Box childGap="lg">
                    <Box fontWeight="medium" color="body-primary">
                      {adderType === AdderType.backupBatteryPriceCap ? 'Backup' : 'Arbitrage'} Battery Caps
                    </Box>
                    <Box gap="lg">
                      <FieldArray
                        name="energyBackupCaps"
                        render={() =>
                          allManufacturers.map((manufacturer: string) => (
                            <Box key={manufacturer} gap="md">
                              <Box fontWeight="medium" color="body-primary">
                                {manufacturer}
                              </Box>
                              <Box marginBottom="md">
                                <Box fontSize="sm" color="body-secondary">
                                  Set the price cap and max term length for{' '}
                                  {adderType === AdderType.backupBatteryPriceCap ? 'Backup' : 'Arbitrage'} Battery
                                  options from {manufacturer}.
                                </Box>
                              </Box>
                              <Field
                                prefix="$"
                                type="number"
                                component={FormikTextInput}
                                label={`Price Cap`}
                                name={`energyBackupCaps.${manufacturer}.maxPrice`}
                                id={`energyBackupCaps.${manufacturer}.maxPrice`}
                                isRequired
                              />
                              <Field
                                type="number"
                                component={FormikTextInput}
                                label={`Max Term Length`}
                                name={`energyBackupCaps.${manufacturer}.maxTermLength`}
                                id={`energyBackupCaps.${manufacturer}.maxTermLength`}
                                isRequired
                              />
                            </Box>
                          ))
                        }
                      />
                    </Box>
                  </Box>
                </Box>
              </Modal.Body>
              <Modal.Footer>
                <Button
                  as="button"
                  onClick={() => {
                    resetForm();
                    closeModal();
                  }}
                  variant="secondary"
                  tone="neutral"
                  size="md"
                  isLoading={isSubmitting}
                >
                  Cancel
                </Button>
                <Button size="md" type="submit" variant="primary" isLoading={isSubmitting}>
                  Add Option
                </Button>
              </Modal.Footer>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};
